/**
 * DepositPendingPage - Payment pending page
 *
 * Shows:
 * - Card number to send money to
 * - Check and Cancel buttons (before payment confirmed)
 * - After payment confirmed: file upload inline, buttons gone, auto-complete on upload
 */

import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Upload, X } from 'lucide-react';

// ============================================
// TYPES
// ============================================

export interface DepositPendingPageProps {
  /** Reference number */
  referenceNumber: string;
  /** Amount being deposited */
  amount: number;
  /** Card number to pay to */
  cardNumber: string;
  /** Card holder name */
  cardHolderName: string;
  /** Bank name (optional) */
  bank?: string;
  /** Bonus percentage */
  bonusPercentage?: number;
  /** Bonus amount */
  bonusAmount?: number;
  /** Expiration time */
  expiresAt?: string;
  /** Whether payment is already confirmed (status = 'paid') */
  isPaid?: boolean;
  /** Whether check status is loading */
  isCheckingStatus?: boolean;
  /** Whether cancel is loading */
  isCancelling?: boolean;
  /** Whether receipt upload is in progress */
  isUploadingReceipt?: boolean;
  /** Callback to check payment status */
  onCheckStatus: () => Promise<{ status: string; action?: string } | null>;
  /** Callback to cancel deposit */
  onCancel: () => Promise<void>;
  /** Callback when receipt needs to be uploaded */
  onUploadReceipt?: (file: File) => Promise<void>;
  /** Callback when deposit flow is done */
  onComplete?: () => void;
}

// ============================================
// TOAST COMPONENT
// ============================================

function Toast({
  message,
  type,
  isVisible,
  onHide,
}: {
  message: string;
  type: 'success' | 'error';
  isVisible: boolean;
  onHide: () => void;
}): React.ReactElement | null {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onHide, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible, onHide]);

  if (!isVisible) return null;

  return (
    <div className={cn('toast', `toast--${type}`)}>
      {message}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function DepositPendingPage({
  referenceNumber: _referenceNumber,
  amount,
  cardNumber,
  cardHolderName,
  bank,
  bonusPercentage,
  bonusAmount,
  isPaid: initialIsPaid = false,
  isCheckingStatus = false,
  isCancelling = false,
  isUploadingReceipt = false,
  onCheckStatus,
  onCancel,
  onUploadReceipt,
  onComplete,
}: DepositPendingPageProps) {
  // Payment confirmed state (can come from prop or from check result)
  const [isPaid, setIsPaid] = useState(initialIsPaid);
  const [isChecking, setIsChecking] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [copied, setCopied] = useState(false);

  // File upload state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Sync with prop
  useEffect(() => {
    if (initialIsPaid) {
      setIsPaid(true);
    }
  }, [initialIsPaid]);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleCopyCard = useCallback(async () => {
    const cardNumberClean = cardNumber.replace(/\s/g, '');
    await navigator.clipboard.writeText(cardNumberClean);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [cardNumber]);

  // Auto-upload when file is selected
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Auto-upload
    if (onUploadReceipt) {
      try {
        await onUploadReceipt(file);
        setToast({ message: 'Depozit uğurla tamamlandı!', type: 'success' });
        setTimeout(() => onComplete?.(), 1500);
      } catch (err) {
        setToast({ message: 'Qəbz yüklənmədi. Yenidən cəhd edin.', type: 'error' });
        // Clear preview on error
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
      }
    }
  }, [onUploadReceipt, onComplete]);

  const handleRemoveFile = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  }, [previewUrl]);

  const handleCheck = async () => {
    setIsChecking(true);
    const result = await onCheckStatus();
    setIsChecking(false);

    if (result?.status === 'paid' || result?.action === 'payment_confirmed') {
      setIsPaid(true);
      setToast({ message: 'Ödənişiniz qəbul edildi!', type: 'success' });
    } else if (result?.status === 'completed' || result?.action === 'already_complete') {
      setToast({ message: 'Ödəniş artıq tamamlanıb!', type: 'success' });
      setTimeout(() => onComplete?.(), 1500);
    } else if (result?.status === 'cancelled' || result?.action === 'deposit_cancelled') {
      setToast({ message: 'Ödəniş ləğv edildi.', type: 'error' });
      setTimeout(() => onComplete?.(), 1500);
    } else {
      setToast({ message: 'Ödəniş hələ alınmayıb. Yenidən cəhd edin.', type: 'error' });
    }
  };

  const handleCancel = async () => {
    await onCancel();
    // Parent will handle navigation
  };

  const hideToast = useCallback(() => setToast(null), []);

  // Format card number with spaces for display
  const formattedCardNumber = cardNumber.replace(/(\d{4})/g, '$1 ').trim();

  return (
    <div className="deposit-pending-page">
      <div className="deposit-pending-page__content">
        <h2 className="deposit-pending-page__title">Ödəniş məlumatları</h2>

        {/* Card display */}
        <div className="deposit-card">
          <div className="deposit-card__amount">{amount} AZN</div>
          {bonusAmount && bonusAmount > 0 && (
            <div className="deposit-card__bonus">+{bonusAmount} AZN bonus ({bonusPercentage}%)</div>
          )}
          <div className="deposit-card__number-wrapper">
            <span className="deposit-card__label">Kart nömrəsi</span>
            <div className="deposit-card__number-row">
              <span className="deposit-card__number">{formattedCardNumber}</span>
              <button
                className={cn('deposit-card__copy', copied && 'deposit-card__copy--copied')}
                onClick={handleCopyCard}
              >
                {copied ? '✓' : '📋'}
              </button>
            </div>
          </div>
          <div className="deposit-card__holder">
            <span className="deposit-card__label">Kart sahibi</span>
            <span className="deposit-card__holder-name">{cardHolderName}</span>
          </div>
          {bank && (
            <div className="deposit-card__bank">
              <span className="deposit-card__label">Bank</span>
              <span className="deposit-card__bank-name">{bank}</span>
            </div>
          )}
        </div>

        {/* Instructions - changes based on state */}
        <p className="deposit-pending-page__instructions">
          {isPaid
            ? 'Ödənişiniz qəbul edildi. Qəbz şəklini yükləyin.'
            : 'Bu karta göstərilən məbləği köçürün və "Yoxla" düyməsinə basın.'}
        </p>

        {/* Receipt upload section - only shown when paid */}
        {isPaid && (
          <div className="deposit-pending-page__upload-section">
            <div className="deposit-pending-page__upload-label">
              Qəbz şəkli
            </div>
            {previewUrl ? (
              <div className="deposit-pending-page__preview">
                <img src={previewUrl} alt="Qəbz" className="deposit-pending-page__preview-image" />
                {!isUploadingReceipt && (
                  <button
                    type="button"
                    className="deposit-pending-page__preview-remove"
                    onClick={handleRemoveFile}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {isUploadingReceipt && (
                  <div className="deposit-pending-page__preview-loading">
                    <div className="deposit-pending-page__spinner" />
                  </div>
                )}
              </div>
            ) : (
              <label htmlFor="receipt-file-upload" className="deposit-pending-page__dropzone">
                <input
                  id="receipt-file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="deposit-pending-page__file-input"
                  disabled={isUploadingReceipt}
                />
                <Upload className="w-8 h-8 text-slate-400 mb-2" />
                <span className="text-sm text-slate-500">Şəkil seçin</span>
              </label>
            )}
          </div>
        )}

        {/* Payment confirmed notice */}
        {isPaid && (
          <p className="deposit-pending-page__paid-notice">
            Ödənişiniz qəbul edildi, ləğv edə bilməzsiniz.
          </p>
        )}

        {/* Action buttons - only shown when NOT paid */}
        {!isPaid && (
          <div className="deposit-pending-page__actions">
            <button
              className={cn(
                'deposit-pending-page__button',
                'deposit-pending-page__button--check',
                (isChecking || isCheckingStatus) && 'deposit-pending-page__button--loading'
              )}
              onClick={handleCheck}
              disabled={isChecking || isCheckingStatus}
            >
              {isChecking || isCheckingStatus ? (
                <>
                  <span className="deposit-pending-page__button-spinner" />
                  Yoxlanılır...
                </>
              ) : (
                'Yoxla'
              )}
            </button>
            <button
              className="deposit-pending-page__button deposit-pending-page__button--decline"
              onClick={handleCancel}
              disabled={isChecking || isCancelling}
            >
              {isCancelling ? 'Ləğv edilir...' : 'Ləğv et'}
            </button>
          </div>
        )}
      </div>

      {/* Toast */}
      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'success'}
        isVisible={!!toast}
        onHide={hideToast}
      />
    </div>
  );
}
